<template>
  <div class="row distribution-access">
    <!-- <img alt="Distribution Access" src="../assets/sp2lnob/Distribution_Access.png" /> -->
    <h3 class="ps-5 pt-2">{{ access_or_prevalence }} {{ access_or_prevalence === 'Access' ? 'to' : 'of' }} {{ indicatorName.toLowerCase() }} Before and After Simulation</h3>
    <h4 class="ps-5"><i>Average {{ access_or_prevalence.toLowerCase() }} as red line</i></h4>
    <dot-plot-chart v-if="chartData.data.length" :data="chartData"></dot-plot-chart>
  </div>
</template>

<script>
import DotPlotChart from '@wc/dot-plot-chart/dot-plot-chart.vue';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'DistributionAccess',
  components: { DotPlotChart },
  props: {
    data: {
      type: Object,
      default: null
    }
  },
  data: function () {
    return {
      indicatorName: '',
      access_or_prevalence: 'Access',
      chartData: {
        x_label: '% Access',
        y_label: '',
        x_unit_label: '%',
        sort_order: 'ASC',
        colors: {
          fa_color: '#4979A6',
          fb_color: '#FFAD80',
          mean_color: '#FF0000',
          average: '#C4C4C4',
          difference: '#EEE',
          gap_increase: '#FFAD80',
          gap_same: '#EEE',
          gap_decrease: '#A0CDF8',
          grey_colors: ['#D4D4D4', '#C4C4C4', '#B4B4B4', '#A4A4A4', '#949494', '#848484', '#747474', '#646464', '#545454', '#444444']
        },
        tooltip: {
          fa: '<strong>[[internal#label]]</strong>\r\n<br />\r\nMean:  [[internal#mean.toFixed(0)]]%',
          fb: '<strong>[[internal#label]]</strong>\r\n<br />\r\nMean:  [[internal#mean.toFixed(0)]]%',
          mean: ''
        },
        total_circles: 4,
        data: []
      },
      orgColors: {
        fa_color: '#4979A6',
        fb_color: '#FFAD80',
        grey_colors: ['#D4D4D4', '#C4C4C4', '#B4B4B4', '#A4A4A4', '#949494', '#848484', '#747474', '#646464', '#545454', '#444444']
      },
      rawData: null
    };
  },
  computed: {
    ...mapGetters('analysis', [
      'getSp2lnobConfigs'
    ]),
    ...mapState('analysis', [
      'sp2lnobConfigs'
    ])
  },
  watch: {
    data: {
      handler: function (data) {
        this.rawData = data;
        if (Object.values(this.getSp2lnobConfigs.detailScenario1.circumstances_labels).length > 0) {
          this.formatData(data);
        }
      }
    },
    sp2lnobConfigs: {
      handler: function () {
        if (Object.values(this.getSp2lnobConfigs.detailScenario1.circumstances_labels).length > 0) {
          this.formatData(this.rawData);
        }
      }
    }
  },
  methods: {
    formatData: function (data) {
      // console.log(data);
      const largestN = Math.max(...data.map(d => d.n));
      const currentData = data.filter(d => d.category === 'Current');
      const simulatedData = data.filter(d => d.category === 'Simulated');
      // console.log(currentData);
      // console.log(simulatedData);
      const newChartData = JSON.parse(JSON.stringify(this.chartData));
      newChartData.total_circles = currentData.length;
      newChartData.data = [{}, {}];
      newChartData.data[0].label = 'With social protection';
      newChartData.data[1].label = 'Without social protection';
      newChartData.data[0].mean = simulatedData[0].mean_pred * 100;
      newChartData.data[1].mean = currentData[0].mean_pred * 100;
      newChartData.data[0].circles = simulatedData.map(sd => ({ node: Math.round(sd.pred * 100), n: sd.n, r: Math.max(Math.round(sd.n / largestN * 20), 2), d: sd.pred * 100 }));
      newChartData.data[1].circles = currentData.map(cd => ({ node: Math.round(cd.pred * 100), n: cd.n, r: Math.max(Math.round(cd.n / largestN * 20), 2), d: cd.pred * 100 }));
      newChartData.data[0].largest_r = Math.max(...data.map(d => Math.round(d.n / largestN * 20)));
      newChartData.data[1].largest_r = Math.max(...data.map(d => Math.round(d.n / largestN * 20)));
      newChartData.data[0].circles.sort((a, b) => a.d - b.d);
      newChartData.data[1].circles.sort((a, b) => a.d - b.d);
      newChartData.tooltip.mean = '<strong>' + this.getSp2lnobConfigs.detailScenario1.outcome_variable_obj.name + '</strong>\r\n<br />\r\nMean:  [[internal#mean.toFixed(1)]]%';
      let i = 0;
      for (const c of newChartData.data[0].circles) {
        newChartData.data[0]['val' + i] = c.d;
        newChartData.data[0]['n' + i] = Math.round(c.n).toLocaleString();
        newChartData.data[0]['clabel' + i] = this.getSp2lnobConfigs.detailScenario1.circumstances_labels[c.node];
        newChartData.tooltip['c' + i] = '<strong>[[internal#clabel' + i + ']]</strong>\r\n<br />\r\n' + this.getSp2lnobConfigs.detailScenario1.outcome_variable_obj.name + ':  [[internal#val' + i + '.toFixed(0)]]%\r\n<br />\r\nSize of group:  [[internal#n' + i + ']]';
        i++;
      }
      let j = 0;
      for (const c of newChartData.data[1].circles) {
        newChartData.data[1]['val' + j] = c.d;
        newChartData.data[1]['n' + j] = Math.round(c.n).toLocaleString();
        newChartData.data[1]['clabel' + j] = this.getSp2lnobConfigs.detailScenario1.circumstances_labels[c.node];
        j++;
      }
      // @Note: for the negative indicator, invert the colors
      if (this.getSp2lnobConfigs.detailScenario1.outcome_variable_obj.type === 'negative') {
        this.access_or_prevalence = 'Prevalence';
        newChartData.x_label = '% Prevalence';
        newChartData.colors.fa_color = this.orgColors.fb_color;
        newChartData.colors.fb_color = this.orgColors.fa_color;
        newChartData.colors.grey_colors = this.orgColors.grey_colors.slice(0, newChartData.total_circles - 2).reverse();
      } else {
        this.access_or_prevalence = 'Access';
        newChartData.x_label = '% Access';
        newChartData.colors.fa_color = this.orgColors.fa_color;
        newChartData.colors.fb_color = this.orgColors.fb_color;
        newChartData.colors.grey_colors = this.orgColors.grey_colors.slice(0, newChartData.total_circles - 2);
      }
      // console.log(newChartData);

      this.chartData = JSON.parse(JSON.stringify(newChartData));
      this.indicatorName = this.getSp2lnobConfigs.detailScenario1.outcome_variable_obj.name || 'Clean Fuel';
    }
  }
};
</script>

<style lang="css">
.distribution-access img {
  width: 100%;
}
.distribution-access .container-box-plot-chart .container-chart {
  margin: auto;
}
</style>
